import { defineModule } from '@/js/utils/helpers';
import { toggleModal, type ModalId } from '@/js/components/modals';

interface CaseItem {
  client: string;
  metric: string;
  unit: string;
  heading: string;
  quote: string;
  name: string;
  role: string;
  avatar: string;
}

interface Branch {
  label: string;
  cases: CaseItem[];
}

interface CtaLink {
  url: string;
  title: string;
  target: string;
}

interface CtaConfig {
  tag: string;
  title: string;
  text: string;
  demoLabel: string;
  demoModal: string;
  link: CtaLink | null;
}

interface DeckI18n {
  verified: string;
  starsLabel: string;
  caseSingular: string;
  casePlural: string;
  from: string;
  goToCard: string;
}

interface DeckData {
  branches: Branch[];
  cta: CtaConfig;
  i18n: DeckI18n;
}

type DeckItem = CaseItem | { cta: true };

const isCtaItem = (item: DeckItem): item is { cta: true } =>
  (item as { cta?: boolean }).cta === true;

/** Escape a string for safe use as HTML text content. */
const escapeHtml = (value: string): string => {
  const el = document.createElement('div');
  el.textContent = value ?? '';
  return el.innerHTML;
};

/** Escape a string for safe use inside a double-quoted HTML attribute. */
const escapeAttr = (value: string): string =>
  (value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/** Escape a URL for an attribute and neutralise dangerous schemes. */
const safeUrl = (value: string): string => {
  const scheme = (value ?? '').replace(/\s/g, '').slice(0, 20).toLowerCase();
  if (/^(javascript|data|vbscript):/.test(scheme)) return '#';
  return escapeAttr(value);
};

const pad = (n: number): string => (n < 10 ? `0${n}` : `${n}`);

/**
 * Wire up a single casedeck instance. Mirrors the server-rendered markup in
 * components/casedeck-card.php — keep both in sync. Returns a teardown fn.
 */
const setupDeck = (root: HTMLElement): (() => void) | null => {
  const dataEl = root.querySelector<HTMLScriptElement>('[data-casedeck-data]');
  const stage = root.querySelector<HTMLElement>('[data-deck-stage]');
  const countEl = root.querySelector<HTMLElement>('[data-deck-count]');
  const dotsEl = root.querySelector<HTMLElement>('[data-deck-dots]');
  const prevBtn = root.querySelector<HTMLElement>('[data-deck-prev]');
  const nextBtn = root.querySelector<HTMLElement>('[data-deck-next]');
  const tabs = Array.from(root.querySelectorAll<HTMLElement>('[data-branche]'));

  if (!dataEl || !stage || !countEl || !dotsEl) return null;

  let data: DeckData;
  try {
    data = JSON.parse(dataEl.textContent || '') as DeckData;
  } catch {
    return null;
  }

  const branches = data.branches ?? [];
  if (!branches.length || !data.i18n) return null;
  const { cta, i18n } = data;

  const reduce =
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

  let branche = 0;
  let idx = 0;
  let list: DeckItem[] = [];
  const timers = new Set<number>();

  const caseInner = (
    c: CaseItem,
    pos: number,
    total: number,
    label: string,
  ): string => {
    const avatar = c.avatar
      ? `<img src="${safeUrl(c.avatar)}" alt="${escapeAttr(c.name)}" loading="lazy" />`
      : '';
    const role = c.role
      ? `<span class="person__role">${escapeHtml(c.role)}</span>`
      : '';
    return (
      `<div class="casedeck__card-inner">` +
      `<div class="cd-top"><span class="tag tag--brand">${escapeHtml(label)}</span>` +
      `<span class="cd-counter"><b>${pad(pos + 1)}</b><i>/</i>${pad(total)}</span></div>` +
      `<div class="cd-client"><span class="cd-dot"></span>${escapeHtml(c.client)}</div>` +
      `<div class="cd-metric"><span class="cd-metric__v">${escapeHtml(c.metric)}</span>` +
      `<span class="cd-metric__u">${escapeHtml(c.unit)}</span></div>` +
      `<h3 class="cd-title">&bdquo;${escapeHtml(c.heading)}&rdquo;</h3>` +
      `<p class="cd-quote">${escapeHtml(c.quote)}</p>` +
      `<div class="cd-foot"><div class="person"><span class="avatar">${
        avatar
      }</span><span class="person__meta"><span class="person__name">${escapeHtml(c.name)}</span>${
        role
      }</span></div>` +
      `<span class="cd-verified"><span class="stars" aria-label="${escapeAttr(i18n.starsLabel)}">★★★★★</span>${escapeHtml(i18n.verified)}</span>` +
      `</div></div>`
    );
  };

  const closingInner = (label: string): string => {
    const title = (cta.title ?? '').replace(/\{branche\}/g, () =>
      label.toLowerCase(),
    );
    const tag = cta.tag
      ? `<span class="tag tag--yellow">${escapeHtml(cta.tag)}</span>`
      : '';
    const text = cta.text
      ? `<p class="cd-cta__text">${escapeHtml(cta.text)}</p>`
      : '';
    const targetAttr = cta.link?.target
      ? ` target="${escapeAttr(cta.link.target)}"`
      : '';
    const secondary =
      cta.link && cta.link.url
        ? `<a href="${safeUrl(cta.link.url)}"${targetAttr}><span class="btn btn--ghost text-white">${escapeHtml(cta.link.title ?? '')}</span></a>`
        : '';
    const primary = `<button type="button" class="btn btn--primary" data-open-modal="${escapeAttr(cta.demoModal)}">${escapeHtml(cta.demoLabel)}</button>`;
    return `<div class="casedeck__card-inner casedeck__card-inner--cta">${tag}<h3 class="cd-cta__title">${escapeHtml(title)}</h3>${text}<div class="cd-cta__btns">${primary}${secondary}</div></div>`;
  };

  const pileHtml = (): string =>
    '<div class="casedeck__grounding" aria-hidden="true"></div>' +
    '<span class="casedeck__back casedeck__back--1" aria-hidden="true"></span>' +
    '<span class="casedeck__back casedeck__back--2" aria-hidden="true"></span>' +
    '<span class="casedeck__back casedeck__back--3" aria-hidden="true"></span>' +
    '<span class="casedeck__back casedeck__back--4" aria-hidden="true"></span>';

  const activeCardHtml = (animClass: string): string => {
    const item = list[idx];
    const { label } = branches[branche];
    const inner = isCtaItem(item)
      ? closingInner(label)
      : caseInner(item, idx, list.length - 1, label);
    const ctaClass = isCtaItem(item) ? ' casedeck__card--cta' : '';
    return `<div class="casedeck__card${ctaClass}${
      animClass ? ` ${animClass}` : ''
    }" data-deck-active>${inner}</div>`;
  };

  const buildList = (): void => {
    list = [...branches[branche].cases, { cta: true }];
  };

  /**
   * Size the stage to the tallest card in the branche. Cards are stacked
   * absolutely, so they all share one height; the CSS floors it at the design's
   * 472px and only grows past that when the content needs it.
   */
  const measure = (): void => {
    const probe = document.createElement('div');
    probe.style.cssText =
      'position:absolute;left:0;right:0;top:0;height:auto;visibility:hidden;pointer-events:none';
    stage.appendChild(probe);

    const { label } = branches[branche];
    let tallest = 0;
    list.forEach((item, i) => {
      probe.className = `casedeck__card${isCtaItem(item) ? ' casedeck__card--cta' : ''}`;
      probe.innerHTML = isCtaItem(item)
        ? closingInner(label)
        : caseInner(item, i, list.length - 1, label);
      tallest = Math.max(tallest, probe.offsetHeight);
    });

    probe.remove();

    // A hidden or zero-width stage measures 0. Leaving the variable unset lets
    // the CSS fall back to --cd-card-unmeasured; writing 0px would suppress it.
    if (tallest > 0) {
      root.style.setProperty('--cd-measured-h', `${Math.ceil(tallest)}px`);
    } else {
      root.style.removeProperty('--cd-measured-h');
    }
  };

  const renderCount = (): void => {
    const n = list.length - 1;
    const noun = n === 1 ? i18n.caseSingular : i18n.casePlural;
    countEl.innerHTML = `${n} ${escapeHtml(noun)} ${escapeHtml(i18n.from)} <strong>${escapeHtml(
      branches[branche].label,
    )}</strong>`;
  };

  const renderDots = (): void => {
    let html = '';
    for (let i = 0; i < list.length; i += 1) {
      html += `<button type="button" class="casedeck__dot${
        i === idx ? ' is-active' : ''
      }" data-dot="${i}" aria-label="${escapeAttr(`${i18n.goToCard} ${i + 1}`)}"></button>`;
    }
    dotsEl.innerHTML = html;
  };

  const paint = (dir: number): void => {
    const prevActive = stage.querySelector<HTMLElement>('[data-deck-active]');
    let inClass = '';
    if (!reduce && dir !== 0) {
      inClass = dir >= 0 ? 'is-in-next' : 'is-in-prev';
    }
    if (prevActive && dir !== 0 && !reduce) {
      prevActive.removeAttribute('data-deck-active');
      prevActive.classList.add(dir >= 0 ? 'is-out-next' : 'is-out-prev');
      prevActive.style.zIndex = '36';
      const timer = window.setTimeout(() => {
        prevActive.remove();
        timers.delete(timer);
      }, 460);
      timers.add(timer);
      stage.insertAdjacentHTML('beforeend', activeCardHtml(inClass));
    } else {
      stage.innerHTML = pileHtml() + activeCardHtml(inClass);
    }
  };

  const go = (dir: number): void => {
    idx = (idx + dir + list.length) % list.length;
    paint(dir);
    renderDots();
  };

  const jump = (n: number): void => {
    if (n === idx) return;
    const dir = n > idx ? 1 : -1;
    idx = n;
    paint(dir);
    renderDots();
  };

  const setBranche = (key: number): void => {
    if (key === branche) return;
    branche = key;
    idx = 0;
    buildList();
    measure();
    tabs.forEach((tab) => {
      const on = Number(tab.getAttribute('data-branche')) === key;
      tab.classList.toggle('is-active', on);
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    renderCount();
    stage.innerHTML = pileHtml() + activeCardHtml('');
    renderDots();
  };

  let disposed = false;
  let measuredAt = 0;

  // A narrower stage wraps the quote further, so the tallest card changes.
  const remeasure = (): void => {
    if (disposed || stage.clientWidth === measuredAt) return;
    measuredAt = stage.clientWidth;
    measure();
  };

  const resize = new ResizeObserver(remeasure);

  const onPrev = (): void => go(-1);
  const onNext = (): void => go(1);
  const onDots = (event: Event): void => {
    const dot = (event.target as HTMLElement).closest<HTMLElement>(
      '[data-dot]',
    );
    if (dot) jump(Number(dot.getAttribute('data-dot')));
  };
  const onStageClick = (event: Event): void => {
    const target = event.target as HTMLElement;
    // The closing CTA card is injected client-side, so its modal button is
    // never picked up by the modals module's setup() — wire it here.
    const modalBtn = target.closest<HTMLElement>('[data-open-modal]');
    if (modalBtn) {
      const id = modalBtn.getAttribute('data-open-modal');
      if (id) toggleModal(id as ModalId, true);
      return;
    }
    if (target.closest('a, button')) return;
    if (target.closest('[data-deck-active]')) go(1);
  };
  const tabBindings = tabs.map((tab) => {
    const handler = (): void =>
      setBranche(Number(tab.getAttribute('data-branche')));
    tab.addEventListener('click', handler);
    return { tab, handler };
  });

  prevBtn?.addEventListener('click', onPrev);
  nextBtn?.addEventListener('click', onNext);
  dotsEl.addEventListener('click', onDots);
  stage.addEventListener('click', onStageClick);
  resize.observe(stage);

  buildList();
  renderCount();
  remeasure();
  stage.innerHTML = pileHtml() + activeCardHtml('');
  renderDots();

  // Text metrics shift once the webfonts land, and with them the tallest card.
  document.fonts?.ready.then(() => {
    if (!disposed) measure();
  });

  return () => {
    disposed = true;
    timers.forEach((timer) => window.clearTimeout(timer));
    timers.clear();
    resize.disconnect();
    prevBtn?.removeEventListener('click', onPrev);
    nextBtn?.removeEventListener('click', onNext);
    dotsEl.removeEventListener('click', onDots);
    stage.removeEventListener('click', onStageClick);
    tabBindings.forEach(({ tab, handler }) =>
      tab.removeEventListener('click', handler),
    );
  };
};

let cleanups: Array<() => void> = [];

export default defineModule(
  () => {
    cleanups = Array.from(
      document.querySelectorAll<HTMLElement>('[data-casedeck]'),
    )
      .map(setupDeck)
      .filter((fn): fn is () => void => typeof fn === 'function');
  },
  () => {
    cleanups.forEach((fn) => fn());
    cleanups = [];
  },
);
